import { createSlice } from "@reduxjs/toolkit";

import { ORGANIZATION_ID_FIELD } from "Constants/constants";
import { setDeep } from "Libs/objectAccess";
import { isJson, normalize } from "Libs/utils";
import { getCommonError } from "Reducers/sliceFactory";

import { CreateOrganizationNamespace } from "./constants";
import {
  getOrganization,
  createOrganization,
  createOrganizationFromLabel,
  deleteOrganization,
  loadOrganizationEstimate,
  updateOrganization
} from "./thunks";

import type { OrganizationState } from "./types";
import type { Organization } from "@packages/client";
import type { PayloadAction } from "@reduxjs/toolkit";
import { loadOrganizations } from "./thunks/loadOrganizations.thunk";

export const initialState: OrganizationState = {
  loading: false,
  loaded: false,
  orgByDescriptionField: {},
  data: {},
  list: [],
  errors: {},
  update: {},
  inflight: {},
  _errors: {},
  [CreateOrganizationNamespace]: undefined,
  organizationLoadingError: undefined,
  loadOrganizationsError: undefined,
  deleted: {
    loading: false
  },
  organizationEstimate: {}
};

const organizationReducer = createSlice({
  name: "app/organization",
  initialState,
  reducers: {
    clearOrganizationError(state) {
      state.organizationLoadingError = undefined;
    },
    clearCreatedOrganization(state) {
      state[CreateOrganizationNamespace] = undefined;
    },
    clearOrganizationState(state) {
      state.orgByDescriptionField = {};
      state.data = {};
      state.list = [];
    },
    clearErrors(state) {
      state.errors = {};
    },
    invalidateOrganization(
      state,
      action: PayloadAction<{ organizationDescriptionId: string }>
    ) {
      const { organizationDescriptionId } = action.payload;
      delete state.orgByDescriptionField[organizationDescriptionId];
    }
  },
  extraReducers: builder => {
    builder
      // GET ORGANIZATION
      .addCase(getOrganization.pending, state => {
        state.loading = true;
      })
      .addCase(getOrganization.fulfilled, (state, action) => {
        const { organization } = action.payload ?? {};
        state.loading = false;
        state.errors = {};
        if (organization) {
          setDeep(
            state,
            ["orgByDescriptionField", organization[ORGANIZATION_ID_FIELD]],
            organization
          );
          setDeep(state, ["data", organization.id], organization);
          state.list.push(organization.id);
        }
      })
      .addCase(getOrganization.rejected, (state, action) => {
        state.loading = false;
        state.organizationLoadingError = action.payload?.error;
      })
      // CREATE ORGANIZATION
      .addCase(createOrganization.pending, state => {
        setDeep(state, ["inflight", CreateOrganizationNamespace], true);

        setDeep(state, ["_errors", CreateOrganizationNamespace], undefined);
      })
      .addCase(
        createOrganization.fulfilled,
        (state, action: PayloadAction<Organization>) => {
          setDeep(state, ["orgByDescriptionField"], {
            ...state.orgByDescriptionField,
            ...normalize([action.payload], ORGANIZATION_ID_FIELD)
          });

          setDeep(state, ["data"], {
            ...state.data,
            ...normalize([action.payload], "id")
          });

          setDeep(state, ["inflight", CreateOrganizationNamespace], false);

          setDeep(state, [CreateOrganizationNamespace], action.payload);

          setDeep(state, ["list"], [...state.list, action.payload.id]);
        }
      )
      .addCase(createOrganization.rejected, (state, action) => {
        setDeep(state, ["inflight", CreateOrganizationNamespace], false);

        setDeep(
          state,
          ["_errors", CreateOrganizationNamespace],
          action.payload
        );
      })
      // CREATE ORGANIZATION FROM LABEL
      .addCase(createOrganizationFromLabel.pending, state => {
        state.inflight[CreateOrganizationNamespace] = true;
        state._errors[CreateOrganizationNamespace] = undefined;
      })
      .addCase(
        createOrganizationFromLabel.fulfilled,
        (state, action: PayloadAction<Organization>) => {
          const org = action.payload;
          state.orgByDescriptionField[org[ORGANIZATION_ID_FIELD]] = org;
          state.data[org.id] = org;
          state.inflight[CreateOrganizationNamespace] = false;
          state[CreateOrganizationNamespace] = org;
          state.list.push(org.id);
        }
      )
      .addCase(createOrganizationFromLabel.rejected, (state, action) => {
        state.inflight[CreateOrganizationNamespace] = false;
        state._errors[CreateOrganizationNamespace] = action.payload;
      })
      // DELETE ORGANIZATION
      .addCase(deleteOrganization.pending, state => {
        setDeep(state, ["deleted"], {
          ...state.deleted,
          loading: true
        });
      })
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        if (action.payload)
          setDeep(state, ["deleted"], {
            ...state.deleted,
            [action.payload.id]: action.payload,
            loading: false
          });
      })
      .addCase(deleteOrganization.rejected, (state, action) => {
        const commonError = getCommonError(action.payload)?.error.message;
        setDeep(
          state,
          ["errors", action.meta.arg.organizationDescriptionId],
          commonError
        );
        setDeep(state, ["deleted"], {
          ...state.deleted,
          loading: false
        });
      })
      // LOAD ORGANIZATION ESTIMATE
      .addCase(loadOrganizationEstimate.pending, (state, action) => {
        const organizationId = action.meta.arg.organization.name;
        setDeep(state, ["organizationEstimate", organizationId], {
          ...state.organizationEstimate[organizationId],
          loading: true
        });
      })
      .addCase(loadOrganizationEstimate.fulfilled, (state, action) => {
        const { organizationId, current, future } = action.payload ?? {};

        if (organizationId && current && future) {
          setDeep(state, ["organizationEstimate", organizationId], {
            current,
            future,
            loading: false
          });
        }
      })
      .addCase(loadOrganizationEstimate.rejected, (state, action) => {
        const commonError = getCommonError(action.payload)?.error.message;
        const organizationId = action.meta.arg.organization.name;

        if (organizationId) {
          setDeep(state, ["organizationEstimate", organizationId], {
            ...state.organizationEstimate[organizationId],
            loading: false
          });
        }
        setDeep(
          state,
          ["errors", action.meta.arg.organization.name],
          commonError
        );
      })
      // UPDATE ORGANIZATION
      .addCase(updateOrganization.pending, state => {
        state.loading = true;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.errors = {};

        if (action?.payload?.organization) {
          setDeep(state, ["update"], {
            ...state.update,
            [action.payload.organization.id]: true
          });

          state.list = [...state.list, action.payload.organization.id];

          setDeep(state, ["orgByDescriptionField"], {
            ...state.orgByDescriptionField,
            ...normalize([action.payload.organization], ORGANIZATION_ID_FIELD)
          });

          setDeep(state, ["data"], {
            ...state.data,
            ...normalize([action.payload.organization], "id")
          });
        }
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.loading = false;
        const error = isJson(action.payload?.error)
          ? JSON.parse(action.payload.error)
          : action.payload;
        const commonError = getCommonError(error);
        if (commonError) {
          setDeep(
            state,
            ["errors", action.meta.arg.organizationDescriptionId],
            commonError.error.message
          );
        }
      })
      // LOAD ORGANIZATIONS START ** ACTION
      .addCase(loadOrganizations.pending, state => {
        state.loading = true;
        state.loadOrganizationsError = undefined;
      })
      // LOAD ORGANIZATIONS ** ACTION
      .addCase(loadOrganizations.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.loadOrganizationsError = undefined;
        state.errors = {};

        if (action.payload) {
          state.list = action.payload.map(organization => organization.id);

          setDeep(state, ["orgByDescriptionField"], {
            ...state.orgByDescriptionField,
            ...normalize(action.payload, ORGANIZATION_ID_FIELD)
          });

          setDeep(state, ["data"], {
            ...state.data,
            ...normalize(action.payload, "id")
          });
        }
      })
      // CREATE ORGANIZATION FAILURE ** ACTION
      .addCase(loadOrganizations.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.organizationLoadingError = action.payload;
        state.loadOrganizationsError = action.payload;
      });
  }
});

export default organizationReducer.reducer;

export const {
  clearOrganizationError,
  clearCreatedOrganization,
  clearOrganizationState,
  clearErrors,
  invalidateOrganization
} = organizationReducer.actions;
